import {
  Box,
  Container,
  Heading,
  SimpleGrid,
  Text,
  Badge,
  HStack,
  Skeleton,
  VStack,
  Alert,
  AlertIcon,
} from "@chakra-ui/react";
import MirrorCard from "../../components/MirrorCard";

interface CommunityMirrorsProps {
  projects: CommunityMirrorProject[];
  isLoading: boolean;
  error?: string | null;
}

function CommunityMirrors({
  projects,
  isLoading,
  error,
}: CommunityMirrorsProps) {
  if (isLoading) return <CommunityMirrorsSkeleton />;
  if (error) return <CommunityMirrorsError />;
  if (!projects || !Array.isArray(projects) || projects.length === 0)
    return <CommunityMirrorsEmpty />;

  return (
    <CommunityMirrorsLayout projectCount={projects.length}>
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
        {projects.map((project) => (
          <MirrorCard key={project.id} project={project} />
        ))}
      </SimpleGrid>
    </CommunityMirrorsLayout>
  );
}

export default CommunityMirrors;

interface CommunityMirrorsLayoutProps {
  children: React.ReactNode;
  showBadge?: boolean;
  projectCount?: number;
}

function CommunityMirrorsLayout({
  children,
  showBadge = false,
  projectCount = 0,
}: CommunityMirrorsLayoutProps) {
  return (
    <Container maxW="container.xl" py={{ base: 4, md: 8 }}>
      <HStack
        justify="space-between"
        mb={{ base: 4, md: 6 }}
        flexDir={{ base: "column", sm: "row" }}
        align={{ base: "start", sm: "center" }}
        spacing={{ base: 2, sm: 4 }}
      >
        <Heading
          fontSize={{ base: "20px", sm: "24px", md: "32px" }}
          fontWeight="semibold"
        >
          Project Showcase
        </Heading>
        {showBadge && (
          <Badge
            colorScheme="blue"
            p={{ base: 1, md: 2 }}
            px={{ base: 2, md: 4 }}
            borderRadius="md"
            fontSize={{ base: "xs", md: "sm" }}
          >
            {projectCount} Active Projects
          </Badge>
        )}
      </HStack>
      {children}
    </Container>
  );
}

function CommunityMirrorsSkeleton() {
  return (
    <CommunityMirrorsLayout>
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
        {[1, 2, 3, 4].map((i) => (
          <Box key={i} p={6} boxShadow="sm" borderRadius="lg">
            <VStack align="stretch" spacing={4}>
              <Skeleton height="24px" width="200px" />
              <Skeleton height="20px" width="150px" />
              <Skeleton height="32px" width="100%" />
            </VStack>
          </Box>
        ))}
      </SimpleGrid>
    </CommunityMirrorsLayout>
  );
}

function CommunityMirrorsError() {
  return (
    <CommunityMirrorsLayout>
      <Alert
        status="error"
        variant="subtle"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        textAlign="center"
        borderRadius="lg"
        p={6}
      >
        <AlertIcon boxSize="6" mr={0} mb={3} />
        <Text fontWeight="semibold" fontSize="lg" mb={2}>
          Unable to Load Community Mirrors
        </Text>
        <Text>
          We encountered an issue while fetching the decentralized hosting
          projects. This could be due to network connectivity or blockchain node
          synchronization. Please try again later.
        </Text>
      </Alert>
    </CommunityMirrorsLayout>
  );
}

function CommunityMirrorsEmpty() {
  return (
    <CommunityMirrorsLayout>
      <VStack
        spacing={4}
        align="center"
        bg="gray.50"
        p={{ base: 4, md: 8 }}
        borderRadius="lg"
        borderWidth="1px"
        borderColor="gray.200"
      >
        <Text
          fontSize={{ base: "md", md: "lg" }}
          fontWeight="semibold"
          color="gray.700"
        >
          No Active Mirrors Yet
        </Text>
        <Text
          color="gray.600"
          textAlign="center"
          maxW="md"
          fontSize={{ base: "sm", md: "md" }}
        >
          Our community showcase is currently empty. These mirrors represent
          verified projects that demonstrate the power of distributed hosting in
          our ecosystem.
        </Text>
      </VStack>
    </CommunityMirrorsLayout>
  );
}
